import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { toast } from "sonner";
import { Copy, Mail, MessageCircle, GraduationCap, Check, X } from "lucide-react";
import Flag from "../components/Flag";
import RichText from "../components/RichText";

const fmt = (s) => `${(s || 0).toFixed(1)}s`;

export default function Results() {
  const { t, lang, settings } = useApp();
  const [quizzes, setQuizzes] = useState([]);
  const [quizId, setQuizId] = useState("");
  const [rankings, setRankings] = useState([]);
  const [copied, setCopied] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(true);

  useEffect(() => {
    api.get("/quizzes", { params: { type: "competition" } }).then((r) => {
      const list = (r.data || []).filter((q) => q.ranking_enabled !== false);
      setQuizzes(list);
      const params = new URLSearchParams(window.location.search);
      const wanted = params.get("quiz");
      const match = wanted && list.find((q) => q.id === wanted);
      if (match) setQuizId(match.id);
      else if (list.length > 0) setQuizId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!quizId) return;
    api.get(`/rankings/${quizId}`).then((r) => setRankings(r.data || []));
  }, [quizId]);

  const tr = (pt, en) => (lang !== "en" ? pt : en);
  const selectedQuiz = quizzes.find((q) => q.id === quizId);
  const contestName = selectedQuiz ? (lang === "en" ? selectedQuiz.name_en : (selectedQuiz.name_br || selectedQuiz.name_pt)) : "";
  const inviteLink = `${window.location.origin}/enroll`;
  const inviteMessage = tr(
    `Olá! Convido a tua turma a participar no concurso ${contestName || "NATALMÁTICO"}. Inscreve a turma aqui: ${inviteLink}`,
    `Hi! I invite your class to join the ${contestName || "X-MATHS Challenge"} contest. Enroll your class here: ${inviteLink}`
  );

  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        const ta = document.createElement("textarea");
        ta.value = inviteLink;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(tr("Link copiado!", "Link copied!"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(tr("Não foi possível copiar", "Could not copy"));
    }
  };

  const waHref = `https://wa.me/?text=${encodeURIComponent(inviteMessage)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(tr("Convite para o NATALMÁTICO", "Invitation to X-MATHS Challenge"))}&body=${encodeURIComponent(inviteMessage)}`;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 pt-10 pb-20" data-testid="results-page">
      <RichText k="results_title" as="h1" className="font-fredoka text-4xl md:text-5xl font-bold text-[#0B1D3A] mb-6" />

      <div className="rounded-3xl bg-white p-6 md:p-8 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.1)] border border-[#0B1D3A]/5">
        <div className="mb-5">
          <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-2">{t("results_pick")}</label>
          <select
            value={quizId}
            onChange={(e) => setQuizId(e.target.value)}
            data-testid="results-quiz-select"
            className="w-full px-4 py-3 rounded-2xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito"
          >
            {quizzes.length === 0 && <option>{lang !== "en" ? "Sem concursos" : "No contests"}</option>}
            {quizzes.map((q) => (
              <option key={q.id} value={q.id}>{lang === "en" ? q.name_en : (q.name_br || q.name_pt)}</option>
            ))}
          </select>
        </div>

        {rankings.length === 0 ? (
          <RichText k="results_empty" as="p" className="font-nunito text-[#0B1D3A]/70 py-6 text-center" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-nunito" data-testid="results-table">
              <thead>
                <tr className="text-left text-[#0B1D3A]/60 text-sm uppercase font-bold border-b border-[#0B1D3A]/10">
                  <th className="py-3">{t("results_pos")}</th>
                  <th className="py-3">{t("results_name")}</th>
                  <th className="py-3">{tr("País", "Country")}</th>
                  {settings.show_school_in_ranking && <th className="py-3">{t("results_school")}</th>}
                  <th className="py-3 text-right">{t("results_final_time")}</th>
                </tr>
              </thead>
              <tbody>
                {rankings.map((r, idx) => (
                  <tr key={`${r.position}-${idx}`} className="border-b border-[#0B1D3A]/5 hover:bg-[#FDFBF7]">
                    <td className="py-3 font-fredoka font-bold text-lg text-[#FF6B6B]">{r.position}</td>
                    <td className="py-3 font-fredoka font-bold text-[#0B1D3A]">{r.name}</td>
                    <td className="py-3"><Flag code={r.nationality} lang={lang} /></td>
                    {settings.show_school_in_ranking && <td className="py-3 text-[#0B1D3A]/70">{r.school}</td>}
                    <td className="py-3 text-right font-fredoka font-bold text-[#005F73]">{r.timed_out ? <span className="text-[#9CA3AF]" data-testid={`results-timeout-${r.position}`}>{tr("Tempo esgotado", "Time ran out")}</span> : fmt(r.final_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite another class */}
      {inviteOpen && (
      <div className="mt-6 rounded-3xl bg-[#0B1D3A] text-white p-6 md:p-8 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.25)] relative overflow-hidden" data-testid="invite-class-card">
        <button onClick={() => setInviteOpen(false)} aria-label={tr("Fechar", "Close")} data-testid="invite-close-btn" className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white transition-colors">
          <X size={18} />
        </button>
        <span className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-[#FF6B6B]/20" />
        <span className="absolute right-10 bottom-2 h-16 w-16 rounded-full bg-[#FFD93D]/20" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FF6B6B]"><GraduationCap size={22} /></span>
            <h2 className="font-fredoka text-2xl font-bold">{tr("Convidar outra turma", "Invite another class")}</h2>
          </div>
          <p className="font-nunito text-white/75 mb-5 max-w-xl">
            {tr(
              "Conheces um professor que devia participar? Partilha o link e convida outra turma para o concurso.",
              "Know a teacher who should join? Share the link and invite another class to the contest."
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-3 min-w-0">
              <input
                readOnly
                value={inviteLink}
                data-testid="invite-link-input"
                className="flex-1 bg-transparent outline-none text-white/90 font-nunito text-sm truncate"
                onFocus={(e) => e.target.select()}
              />
              <button onClick={copyLink} data-testid="invite-copy-btn" className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white text-[#0B1D3A] font-bold text-sm hover:bg-white/90 transition-colors">
                {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? tr("Copiado", "Copied") : tr("Copiar", "Copy")}
              </button>
            </div>
            <a href={waHref} target="_blank" rel="noreferrer" data-testid="invite-whatsapp-btn" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#25D366] hover:bg-[#1ebe5a] text-white font-bold transition-colors">
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a href={mailHref} data-testid="invite-email-btn" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#FFD93D] hover:bg-[#f5cd1f] text-[#0B1D3A] font-bold transition-colors">
              <Mail size={18} /> Email
            </a>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
